import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Check, X, AlertTriangle, 
  CheckCircle2, XCircle, Clock, Package, Search
} from 'lucide-react';

const InventoryTab = ({ plants }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [showCountDialog, setShowCountDialog] = useState(false);
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [countList, setCountList] = useState([]);
  const [countSearchTerm, setCountSearchTerm] = useState('');
  
  const [newSessionForm, setNewSessionForm] = useState({
    name: '',
    plant_id: ''
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wms/inventory-sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching inventory sessions:', error);
      toast.error('Eroare la incarcarea sesiunilor de inventar');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async () => {
    try {
      const data = {
        name: newSessionForm.name || `Inventar ${new Date().toLocaleDateString('ro-RO')}`,
        plant_id: newSessionForm.plant_id || null
      };
      await api.post('/wms/inventory-sessions', data);
      toast.success('Sesiune de inventar creata!');
      setShowNewSessionDialog(false);
      setNewSessionForm({ name: '', plant_id: '' });
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la creare sesiune');
    }
  };

  const handleOpenCountDialog = async (session) => {
    setSelectedSession(session);
    try {
      const response = await api.get(`/wms/inventory-sessions/${session.id}/count-list`);
      setCountList(response.data);
      setShowCountDialog(true);
    } catch (error) {
      toast.error('Eroare la incarcarea listei de numarare');
    }
  };

  const handleRecordCount = async (item, countedQuantity) => {
    try {
      await api.post(`/wms/inventory-sessions/${selectedSession.id}/count`, {
        article_id: item.article_id,
        bin_id: item.bin_id,
        counted_quantity: parseFloat(countedQuantity)
      });
      
      // Update local state
      setCountList(prev => prev.map(i => 
        i.article_id === item.article_id && i.bin_id === item.bin_id
          ? { ...i, counted_quantity: parseFloat(countedQuantity), counted: true }
          : i
      ));
      
      toast.success('Numaratoare salvata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleReconcile = async () => {
    try {
      const response = await api.post(`/wms/inventory-sessions/${selectedSession.id}/reconcile`);
      setSelectedSession({ ...selectedSession, discrepancies: response.data.discrepancies, status: 'reconciling' });
      setShowCountDialog(false);
      setShowReconcileDialog(true);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la reconciliere');
    }
  };

  const handleApplyAdjustments = async () => {
    try {
      await api.post(`/wms/inventory-sessions/${selectedSession.id}/apply-adjustments`);
      toast.success('Ajustarile au fost aplicate!');
      setShowReconcileDialog(false);
      setSelectedSession(null);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la aplicare ajustari');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Deschis' },
      counting: { bg: 'bg-yellow-100 text-yellow-700', icon: ClipboardList, label: 'In numarare' },
      reconciling: { bg: 'bg-orange-100 text-orange-700', icon: AlertTriangle, label: 'Reconciliere' },
      closed: { bg: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Inchis' }
    };
    const style = styles[status] || styles.open;
    const Icon = style.icon;
    return (
      <Badge className={style.bg}>
        <Icon className="w-3 h-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const filteredCountList = countList.filter(item =>
    item.article_name?.toLowerCase().includes(countSearchTerm.toLowerCase()) ||
    item.bin_code?.toLowerCase().includes(countSearchTerm.toLowerCase()) ||
    item.article_code?.toLowerCase().includes(countSearchTerm.toLowerCase())
  );

  const countedItems = countList.filter(i => i.counted).length;
  const totalItems = countList.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Inventar Periodic</h3>
          <p className="text-sm text-slate-500">Gestionati sesiunile de inventariere</p>
        </div>
        <Button onClick={() => setShowNewSessionDialog(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Sesiune Noua
        </Button>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Se incarca...</div>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Nu exista sesiuni de inventar</p>
            <p className="text-sm text-slate-400 mt-1">Creati o sesiune noua pentru a incepe inventarierea</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sessions.map((session) => (
            <Card key={session.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{session.name}</h4>
                      <p className="text-sm text-slate-500">
                        Creat de {session.created_by_name} • {new Date(session.created_at).toLocaleDateString('ro-RO')}
                      </p>
                      {session.counts?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-1">
                          {session.counts.length} articole numarate
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(session.status)}
                    {session.status !== 'closed' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenCountDialog(session)}
                      >
                        <ClipboardList className="w-4 h-4 mr-1" />
                        {session.status === 'reconciling' ? 'Vezi Discrepante' : 'Numara'}
                      </Button>
                    )}
                    {session.status === 'reconciling' && (
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          setSelectedSession(session);
                          setShowReconcileDialog(true);
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Finalizeaza
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Session Dialog */}
      <Dialog open={showNewSessionDialog} onOpenChange={setShowNewSessionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sesiune Noua de Inventar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Denumire sesiune</Label>
              <Input
                value={newSessionForm.name}
                onChange={(e) => setNewSessionForm({ ...newSessionForm, name: e.target.value })}
                placeholder={`Inventar ${new Date().toLocaleDateString('ro-RO')}`}
              />
            </div>
            <div className="space-y-2">
              <Label>Fabrica (optional)</Label>
              <Select
                value={newSessionForm.plant_id || "all"}
                onValueChange={(v) => setNewSessionForm({ ...newSessionForm, plant_id: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toate sediile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate sediile</SelectItem>
                  {plants?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSessionDialog(false)}>Anuleaza</Button>
            <Button onClick={handleCreateSession} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Creeaza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Count Dialog */}
      <Dialog open={showCountDialog} onOpenChange={setShowCountDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Numarare Inventar: {selectedSession?.name}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Progres: {countedItems} / {totalItems} articole numarate
            </p>
          </DialogHeader>

          {/* Progress bar */}
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${totalItems > 0 ? (countedItems / totalItems) * 100 : 0}%` }}
            />
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cauta articol sau BIN..."
              value={countSearchTerm}
              onChange={(e) => setCountSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Count List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredCountList.map((item, idx) => (
              <div 
                key={`${item.article_id}-${item.bin_id}`}
                className={`p-3 rounded-lg border ${item.counted ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {item.counted && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />}
                      <span className="font-medium text-slate-900 truncate">{item.article_name}</span>
                      <Badge variant="outline" className="text-xs">{item.article_code}</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      BIN: <span className="font-mono">{item.bin_code}</span> • 
                      Sistem: <span className="font-semibold">{item.system_quantity} {item.unit}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      className="w-24 h-9"
                      placeholder="Cantitate"
                      defaultValue={item.counted_quantity ?? ''}
                      min="0"
                      step="0.01"
                      onBlur={(e) => {
                        if (e.target.value !== '' && e.target.value !== String(item.counted_quantity)) {
                          handleRecordCount(item, e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        }
                      }}
                    />
                    <span className="text-sm text-slate-500 w-10">{item.unit}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCountDialog(false)}>Inchide</Button>
            <Button 
              onClick={handleReconcile}
              disabled={countedItems === 0}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Calculeaza Discrepante
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconcile Dialog */}
      <Dialog open={showReconcileDialog} onOpenChange={setShowReconcileDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Reconciliere Inventar: {selectedSession?.name}</DialogTitle>
          </DialogHeader>

          {selectedSession?.discrepancies?.length > 0 ? (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              <p className="text-sm text-slate-600">
                Au fost gasite {selectedSession.discrepancies.length} discrepante:
              </p>
              {selectedSession.discrepancies.map((disc, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    disc.difference > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{disc.article_name}</p>
                      <p className="text-sm text-slate-500">BIN: {disc.bin_code}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">
                        Sistem: {disc.system_quantity} → Numarat: {disc.counted_quantity}
                      </p>
                      <p className={`font-bold ${disc.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {disc.difference > 0 ? '+' : ''}{disc.difference}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="text-slate-600">Nu exista discrepante!</p>
              <p className="text-sm text-slate-400">Stocul din sistem corespunde cu numaratoarea</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReconcileDialog(false)}>Anuleaza</Button>
            <Button 
              onClick={handleApplyAdjustments}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check className="w-4 h-4 mr-2" />
              Aplica Ajustarile si Inchide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryTab;
